import { useEffect, useMemo } from 'react';

import usePublisher from 'api/usePublisher';
import { EIntervalsRepeat } from 'constants/enums';
import dayjs from 'dayjs';

import Button from 'components/Button/Button';
import { Flex } from 'components/ui/flex';

interface ExchangeRatesUpdaterManagerProps {
  setUpdateExchangeRatesDialogOpen: (open: boolean) => void;
  disabled?: boolean;
}

const ExchangeRatesUpdaterManager: React.FC<
  ExchangeRatesUpdaterManagerProps
> = ({ setUpdateExchangeRatesDialogOpen, disabled }) => {
  const { getPublisherSettings } = usePublisher();

  const nextUpdateDate = useMemo(() => {
    if (!getPublisherSettings.data) return '';

    const scheduler = getPublisherSettings.data.exchangeRatesUpdateScheduler;
    if (!scheduler || scheduler.frequency === EIntervalsRepeat.NONE) {
      return 'Not scheduled';
    }

    const now = dayjs();
    let nextUpdate: dayjs.Dayjs;

    switch (scheduler.frequency) {
      case EIntervalsRepeat.DAILY:
        nextUpdate = now.hour(scheduler.hour).minute(0).second(0);
        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(1, 'day');
        }
        break;

      case EIntervalsRepeat.WEEKLY:
        const targetWeekday = scheduler.weeklyFrequencyFactor;
        nextUpdate = now
          .day(targetWeekday)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);
        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(1, 'week');
        }
        break;

      case EIntervalsRepeat.MONTHLY:
        const targetDay = scheduler.monthlyFrequencyFactor;
        nextUpdate = now
          .date(targetDay)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);
        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(1, 'month');
        }
        break;

      case EIntervalsRepeat.QUARTERLY:
        const currentQuarter = Math.floor(now.month() / 3);
        const quarterMonths = [0, 3, 6, 9];
        const targetMonthQuarterly = quarterMonths[currentQuarter];

        nextUpdate = now
          .month(targetMonthQuarterly)
          .date(1)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);

        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(3, 'month');
        }
        break;

      case EIntervalsRepeat.SEMI_ANNUALLY:
        const currentSemiAnnually = Math.floor(now.month() / 6);
        const semiAnnuallyMonths = [0, 6];
        const targetMonthSemiAnnually = semiAnnuallyMonths[currentSemiAnnually];

        nextUpdate = now
          .month(targetMonthSemiAnnually)
          .date(1)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);

        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(6, 'month');
        }
        break;

      case EIntervalsRepeat.ANNUALLY:
        nextUpdate = now
          .month(0)
          .date(1)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);
        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(1, 'year');
        }
        break;

      default:
        return 'Not scheduled';
    }

    return nextUpdate.format('MMM DD, YYYY [at] HH:mm UTC');
  }, [getPublisherSettings.data]);

  useEffect(() => {
    if (!getPublisherSettings.data) {
      getPublisherSettings.refetch();
    }
  }, [getPublisherSettings.data, getPublisherSettings]);

  return (
    <Flex
      direction="row"
      align="center"
      justify="between"
      className="settings-card mt-4 p-9"
    >
      <div className="max-w-[50%]">
        <h3 className="text-base-cardForeground font-semibold">
          Update Exchange Rates
        </h3>
        <p className="mt-2 small-text">
          Keep exchange rates current by either refreshing them manually or
          scheduling automatic updates. You can schedule these to occur daily,
          weekly, or monthly. Prices will adjust according to the latest rates.
        </p>
      </div>
      <div>
        <div className="buttons-wrapper justify-end">
          <Button
            onClick={() => setUpdateExchangeRatesDialogOpen(true)}
            variant="default"
            type="button"
            disabled={disabled}
          >
            Update
          </Button>
        </div>
        <div className="mt-2">
          <span className="small-text">Next update: {nextUpdateDate}</span>
        </div>
      </div>
    </Flex>
  );
};

export default ExchangeRatesUpdaterManager;
